import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from './paymentWaiting.module.css';
import phonePeIcon from '../../../assets/images/verticaltv/phone-pe.png';
import { NAVIGATION_ROUTES, TIMEOUT_FOR_ONE_SEC } from '../../../constant';
import { useGetSubscriptionStatus } from '../../../api/getSubscriptionStatus';
import upiGroupImages from '../../../assets/images/verticaltv/upi_waiting.svg';
import { CLEVERTAP_EVENTS } from '../../../analytics/trackerEvents/cleverTap/events';
import { CT_PARAMS } from '../../../analytics/trackerEvents/cleverTap/parameters';
import { trackCleverTapEvent } from '../../../utils/cleverTapHelper';
import { FbPixelEvent_StartTrial, FbPixelEvent_Subscribe } from '../../../utils/FbPixel/fbPixel';
import { useTokenStore } from '../../../storage/tokenState';
import { pushGAEvent, GA_EVENTS } from '../../../utils/gaHelper';

const PaymentWaiting: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [orderId, setOrderId] = useState<string | null>(null);
  const [amount, setAmount] = useState<number>(0);
  const [appName, setAppName] = useState<string>('payment app');
  const [isCheckingStatus, setIsCheckingStatus] = useState<boolean>(false);
  const [isTrialFlow, setIsTrialFlow] = useState<boolean>(false);
  const [timeRemaining, setTimeRemaining] = useState<number>(300); // 5 minutes in seconds
  const { checkSubscriptionStatus, isLoading } = useGetSubscriptionStatus();
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollAttemptsRef = useRef<number>(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const { token } = useTokenStore();
  
  // Check if this is UPI collect flow (5 minutes) or normal UPI flow
  const isUpiCollectFlow = sessionStorage.getItem('isUpiCollectFlow') === 'true';
  const maxPollAttempts = isUpiCollectFlow ? 60 : 10; // 5 minutes for UPI collect (60 attempts * 5s = 5 minutes), 10 seconds for normal UPI
  const pollInterval = isUpiCollectFlow ? 5000 : 1000; // Poll every 5 seconds for UPI collect, 1 second for normal UPI
  const maxPollingTime = maxPollAttempts * pollInterval; // Total time in milliseconds
  const initialTimeRemaining = isUpiCollectFlow ? 300 : 10; // 5 minutes for UPI collect, 30 seconds for normal UPI

  // Function to handle timeout navigation (when timer ends)
  const handleTimeoutNavigation = useCallback(() => {
    console.log('[TIMEOUT] Timer ended. Navigating to payment failed screen...');
    
    // Clear stored data
    sessionStorage.removeItem('pendingOrderId');
    sessionStorage.removeItem('paymentInitiated');
    sessionStorage.removeItem('paymentTimestamp');
    sessionStorage.removeItem('pendingPaymentAmount');
    sessionStorage.removeItem('pendingPaymentDisplayAmount');
    sessionStorage.removeItem('mandateUri');
    sessionStorage.removeItem('mandateUriOpened');
    sessionStorage.removeItem('shouldNavigateToProcessing');
    sessionStorage.removeItem('isUpiCollectFlow');
    
    // Stop polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    
    // Clear timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    // Stop timer
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    
    // Navigate to payment failed screen
    navigate('/trial/payment-failed');
  }, [navigate]);

  useEffect(() => {
    // Get orderId from location state or sessionStorage
    const stateOrderId = location.state?.orderId;
    const storedOrderId = sessionStorage.getItem('pendingOrderId');
    
    const finalOrderId = stateOrderId || storedOrderId;
    const finalAmount = location.state?.amount || parseInt(sessionStorage.getItem('pendingPaymentAmount') || '0', 10);
    const finalAppName = location.state?.appName || 'UPI app';
    
    // Check if this is a trial flow by checking display amount
    // For trial: displayAmount is 2, but apiAmount (finalAmount) might be 549
    // So we check the display amount from location state or sessionStorage
    const displayAmount = location.state?.displayAmount || parseInt(sessionStorage.getItem('pendingPaymentDisplayAmount') || '0', 10);
    const isTrial = displayAmount === 2;
    
    if (finalOrderId) {
      setOrderId(finalOrderId);
      setAmount(finalAmount);
      setAppName(finalAppName);
      setIsTrialFlow(isTrial);
      setTimeRemaining(initialTimeRemaining);
      
      // Note: UPI app should already be opened from the previous screen
      // This check is kept as a fallback in case the app wasn't opened
      const storedMandateUri = sessionStorage.getItem('mandateUri');
      const mandateUriOpened = sessionStorage.getItem('mandateUriOpened');
      
      // Only open if it hasn't been opened yet (fallback scenario)
      if (storedMandateUri && !mandateUriOpened) {
        // Mark that we're opening it
        sessionStorage.setItem('mandateUriOpened', 'true');
        
        // Open UPI app with mandate URI after a short delay
        setTimeout(() => {
          try {
            console.log('Opening UPI app from PaymentWaiting screen (fallback)...');
            window.location.href = storedMandateUri;
          } catch (error) {
            console.error('Error opening UPI app:', error);
          }
        }, 500); // Small delay to ensure screen is visible
      }
    } else {
      // No orderId found, go back
      navigate('/trial/subscription-payment');
    }
  }, [location, navigate, initialTimeRemaining]);

  // Function to check subscription status and navigate accordingly
  const checkPaymentStatus = useCallback(async () => {
    // Better check - don't block if isCheckingStatus is true, just skip this attempt
    if (pollAttemptsRef.current >= maxPollAttempts) {
      console.log('[POLLING] Max polling attempts reached, navigating to payment failed screen...');
      
      // Clear stored data
      sessionStorage.removeItem('pendingOrderId');
      sessionStorage.removeItem('paymentInitiated');
      sessionStorage.removeItem('paymentTimestamp');
      sessionStorage.removeItem('pendingPaymentAmount');
     
      sessionStorage.removeItem('mandateUri');
      sessionStorage.removeItem('mandateUriOpened');
      sessionStorage.removeItem('shouldNavigateToProcessing');
      sessionStorage.removeItem('isUpiCollectFlow');
      
      // Stop polling
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      
      // Clear timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      
      // Stop timer
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      
      // Navigate to payment failed screen
      navigate('/trial/payment-failed');
      return;
    }

    // Skip if already checking (but don't block forever)
    if (isCheckingStatus) {
      console.log('[POLLING] Already checking status, skipping this attempt...');
      return;
    }

    setIsCheckingStatus(true);
    pollAttemptsRef.current += 1;

    try {
      console.log(`[POLLING] Attempt ${pollAttemptsRef.current}/${maxPollAttempts} - Checking subscription status...`);
      const response = await checkSubscriptionStatus();

      console.log('[POLLING] Full API Response:', JSON.stringify(response, null, 2));

      if (response?.response?.success) {
        const activeSubscriptionsData = response.response.data?.activeSubscriptionsData;
        
        console.log('[POLLING] activeSubscriptionsData:', activeSubscriptionsData);
        
        // Check if activeSubscriptionsData contains ZUPEE_TV object with isActive: true
        if (activeSubscriptionsData?.ZUPEE_TV?.isActive === true) {
          // Payment successful - navigate to subscribed screen
          console.log('[POLLING] ✅ Subscription is active! Navigating to subscribed screen...');
          
          // Track CleverTap event for successful payment
          const userId = token?.zupeeUserId?.toString() || '';
          const currentOrderId = sessionStorage.getItem('pendingOrderId') || '';
          const paymentAmount = parseInt(sessionStorage.getItem('pendingPaymentAmount') || '0', 10);
          const displayAmount = parseInt(sessionStorage.getItem('pendingPaymentDisplayAmount') || '0', 10);
          const userMobile = token?.signupPhoneNumber || token?.un || '';
          // Use displayAmount to determine trial (displayAmount is 2 for trial, but paymentAmount might be 549)
          const isTrialPayment = displayAmount === 2;
          const paymentType = isTrialPayment ? CT_PARAMS.TRIAL : CT_PARAMS.SUBSCRIPTION;
          
          trackCleverTapEvent(CLEVERTAP_EVENTS.WEB_EVENT_PAYMENT_STATUS_SCREEN_VIEW, {
            [CT_PARAMS.USER_ID]: userId,
            [CT_PARAMS.ORDER_ID]: currentOrderId,
            [CT_PARAMS.STATUS_OF_PAYMENT]: 'success',
            [CT_PARAMS.PACK_AMOUNT]: paymentAmount,
            [CT_PARAMS.TYPE]: paymentType,
            [CT_PARAMS.MOBILE_NUMBER]: userMobile
          });
          
          // Track Facebook Pixel events based on display amount
          // If display amount is 2 rupees, it's a trial (StartTrial event)
          // Otherwise, it's a paid subscription (Subscribe event)
          
          if (paymentAmount === 2) {
       
            // 2 rupees trial - StartTrial event
            // Use displayAmount (2) for the event value, not paymentAmount (which might be 549)
            FbPixelEvent_StartTrial(paymentAmount);
            
            // Track GA event for trial payment success
            pushGAEvent(GA_EVENTS.TRIAL_PAYMENT_SUCCESS, {
              order_id: currentOrderId,
              amount: paymentAmount,
              user_id: userId
            });
          } else {
            // Paid subscription - Subscribe event
            // FbPixelEvent_Subscribe(paymentAmount);

            // Track GA event for subscription payment success
            pushGAEvent(GA_EVENTS.SUBSCRIPTION_PAYMENT_SUCCESS, {
              order_id: currentOrderId,
              amount: paymentAmount,
              user_id: userId,
              user_mobile: userMobile,
            });
          }
          
          // Clear all stored payment data
          sessionStorage.removeItem('pendingOrderId');
          sessionStorage.removeItem('paymentInitiated');
          sessionStorage.removeItem('paymentTimestamp');
          sessionStorage.removeItem('pendingPaymentAmount');
         
          sessionStorage.removeItem('mandateUri');
          sessionStorage.removeItem('mandateUriOpened');
          sessionStorage.removeItem('shouldNavigateToProcessing');
          sessionStorage.removeItem('isUpiCollectFlow');
          
          // Stop polling
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          
          // Clear timeout
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          
          // Stop timer
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
          }
          
          // Navigate to subscribed screen
          navigate('/trial/subscribed', {
            state: { fromPaymentProcessing: true }
          });
          return;
        } else if (activeSubscriptionsData && Object.keys(activeSubscriptionsData).length === 0) {
          // Empty object - check if we've reached max attempts
          if (pollAttemptsRef.current >= maxPollAttempts) {
            console.log('[POLLING] ⚠️ Max polling attempts reached with empty subscription data. Navigating to payment failed screen...');
            
            // Clear stored data
            sessionStorage.removeItem('pendingOrderId');
            sessionStorage.removeItem('paymentInitiated');
            sessionStorage.removeItem('paymentTimestamp');
            sessionStorage.removeItem('pendingPaymentAmount');
            
            sessionStorage.removeItem('mandateUri');
            sessionStorage.removeItem('mandateUriOpened');
            sessionStorage.removeItem('shouldNavigateToProcessing');
            sessionStorage.removeItem('isUpiCollectFlow');
            
            // Stop polling
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
            
            // Clear timeout
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
              timeoutRef.current = null;
            }
            
            // Stop timer
            if (timerIntervalRef.current) {
              clearInterval(timerIntervalRef.current);
              timerIntervalRef.current = null;
            }
            
            // Navigate to payment failed screen
            navigate('/trial/payment-failed');
            return;
          }
          
          // Continue polling if we haven't reached max attempts
          console.log('[POLLING] ⏳ Subscription data is empty, will retry...', {
            attempts: pollAttemptsRef.current,
            maxAttempts: maxPollAttempts
          });
          
          setIsCheckingStatus(false);
          return;
        } else {
          // Subscription not active yet - check if we've reached max attempts
          if (pollAttemptsRef.current >= maxPollAttempts) {
            console.log('[POLLING] ⚠️ Max polling attempts reached without active subscription. Navigating to payment failed screen...');
            
            // Clear stored data
            sessionStorage.removeItem('pendingOrderId');
            sessionStorage.removeItem('paymentInitiated');
            sessionStorage.removeItem('paymentTimestamp');
            sessionStorage.removeItem('pendingPaymentAmount');
          
            sessionStorage.removeItem('mandateUri');
            sessionStorage.removeItem('mandateUriOpened');
            sessionStorage.removeItem('shouldNavigateToProcessing');
            sessionStorage.removeItem('isUpiCollectFlow');
            
            // Stop polling
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
            
            // Clear timeout
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
              timeoutRef.current = null;
            }
            
            // Stop timer
            if (timerIntervalRef.current) {
              clearInterval(timerIntervalRef.current);
              timerIntervalRef.current = null;
            }
            
            // Navigate to payment failed screen
            navigate('/trial/payment-failed');
            return;
          }
          
          // Continue polling if we haven't reached max attempts
          console.log('[POLLING] ⏳ Subscription not active yet, will retry...', {
            hasZUPEE_TV: !!activeSubscriptionsData?.ZUPEE_TV,
            isActive: activeSubscriptionsData?.ZUPEE_TV?.isActive,
            attempts: pollAttemptsRef.current,
            maxAttempts: maxPollAttempts
          });
          
          setIsCheckingStatus(false);
          return;
        }
      } else {
        // API returned failed response
        const responseAny = response as any;
        console.warn('[POLLING] ❌ API returned failed response:', {
          success: response?.response?.success,
          message: responseAny?.response?.message,
          error: responseAny?.response?.error,
          attempts: pollAttemptsRef.current
        });
        
        // If we've reached max attempts, navigate to failed screen
        if (pollAttemptsRef.current >= maxPollAttempts) {
          console.error('[POLLING] Max polling attempts reached. Navigating to payment failed screen...');
          
          // Clear stored data
          sessionStorage.removeItem('pendingOrderId');
          sessionStorage.removeItem('paymentInitiated');
          sessionStorage.removeItem('paymentTimestamp');
          sessionStorage.removeItem('pendingPaymentAmount');
          
          sessionStorage.removeItem('mandateUri');
          sessionStorage.removeItem('mandateUriOpened');
          sessionStorage.removeItem('shouldNavigateToProcessing');
          sessionStorage.removeItem('isUpiCollectFlow');
          
          // Stop polling
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          
          // Clear timeout
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          
          // Stop timer
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
          }
          
          // Navigate to payment failed screen
          navigate('/trial/payment-failed');
          return;
        } else {
          // Continue polling on failed response (might be temporary)
          console.warn('[POLLING] Will retry after failed response...');
          setIsCheckingStatus(false);
          return;
        }
      }
    } catch (error: any) {
      console.error('[POLLING] ❌ Error checking subscription status:', {
        error,
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
        attempts: pollAttemptsRef.current,
        maxAttempts: maxPollAttempts
      });
      
      // Always reset isCheckingStatus in catch block
      setIsCheckingStatus(false);
      
      // If we've exhausted all attempts, navigate to payment failed screen
      if (pollAttemptsRef.current >= maxPollAttempts) {
        console.error('[POLLING] Max polling attempts reached. Navigating to payment failed screen...');
        
        // Clear stored data
        sessionStorage.removeItem('pendingOrderId');
        sessionStorage.removeItem('paymentInitiated');
        sessionStorage.removeItem('paymentTimestamp');
        sessionStorage.removeItem('pendingPaymentAmount');
       
        sessionStorage.removeItem('mandateUri');
        sessionStorage.removeItem('mandateUriOpened');
        sessionStorage.removeItem('shouldNavigateToProcessing');
        sessionStorage.removeItem('isUpiCollectFlow');
        
        // Stop polling
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        
        // Clear timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        
        // Stop timer
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
        
        // Navigate to payment failed screen
        navigate('/trial/payment-failed');
      }
      // Don't return here - let polling continue if attempts < max
    }
  }, [isCheckingStatus, checkSubscriptionStatus, navigate, maxPollAttempts, token]);

  // Start polling when component mounts with orderId
  useEffect(() => {
    // Get orderId from location state or sessionStorage
    const stateOrderId = location.state?.orderId;
    const storedOrderId = sessionStorage.getItem('pendingOrderId');
    
    const finalOrderId = stateOrderId || storedOrderId;
    
    // Set orderId if available (but don't require it for polling)
    if (finalOrderId) {
      setOrderId(finalOrderId);
    }
    
    // Check if UPI app was opened or payment is in progress (this indicates user should see polling)
    const mandateUriOpened = sessionStorage.getItem('mandateUriOpened');
    
    // Start polling if:
    // 1. mandateUriOpened is set (user returned from payment or payment is in progress)
    // 2. OR orderId exists (payment was initiated)
    // Polling doesn't require orderId - it just checks subscription status
    if ((mandateUriOpened === 'true' || finalOrderId) && !pollingIntervalRef.current) {
      console.log('Starting subscription status polling...', {
        mandateUriOpened,
        hasOrderId: !!finalOrderId,
        orderId: finalOrderId
      });
      
      // Reset polling attempts
      pollAttemptsRef.current = 0;
      
      // Start timer countdown
      setTimeRemaining(initialTimeRemaining);
      
      // Start timer countdown interval
      timerIntervalRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            // Timer ended, navigate to failed screen
            if (timerIntervalRef.current) {
              clearInterval(timerIntervalRef.current);
              timerIntervalRef.current = null;
            }
            handleTimeoutNavigation();
            return 0;
          }
          return prev - 1;
        });
      }, 1000); // Update every second
      
      // Start polling after a short delay to ensure component is ready
      const startPolling = () => {
        // Check if polling already started (prevent duplicate starts)
        if (pollingIntervalRef.current) {
          console.log('Polling already started, skipping...');
          return;
        }
        
        // Start immediate check
        checkPaymentStatus();
        
        // Set up polling interval
        pollingIntervalRef.current = setInterval(() => {
          // Always check if we've reached max attempts first
          if (pollAttemptsRef.current >= maxPollAttempts) {
            // Stop polling if max attempts reached
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
            
            // Final check to ensure we navigate if max attempts reached
            // This handles the case where the last attempt didn't trigger navigation
            console.log('[POLLING] Max attempts reached in interval. Performing final check and navigation...');
            checkPaymentStatus();
            return;
          }
          
          // Continue polling if we haven't reached max attempts
          checkPaymentStatus();
        }, pollInterval);
        
        // Set up timeout as backup mechanism to ensure navigation after max time
        timeoutRef.current = setTimeout(() => {
          console.log('[TIMEOUT] Max polling time elapsed. Triggering timeout navigation...');
          handleTimeoutNavigation();
        }, maxPollingTime);
      };
      
      // Start polling after a short delay
      setTimeout(startPolling, 1000);
    } else if (!mandateUriOpened && !finalOrderId) {
      // No orderId and no mandateUriOpened flag - likely navigated here by mistake
      console.log('No payment in progress, navigating back to payment page...');
      navigate('/trial/subscription-payment');
    }
  }, [location, navigate, checkPaymentStatus, handleTimeoutNavigation, initialTimeRemaining, maxPollAttempts, pollInterval, maxPollingTime]);

  // Cleanup polling interval, timeout, and timer on unmount
  useEffect(() => {
    return () => {
      // Clean up polling interval when component unmounts
      if (pollingIntervalRef.current) {
        console.log('Cleaning up polling interval on unmount...');
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      // Clean up timeout when component unmounts
      if (timeoutRef.current) {
        console.log('Cleaning up timeout on unmount...');
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      // Clean up timer when component unmounts
      if (timerIntervalRef.current) {
        console.log('Cleaning up timer on unmount...');
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, []);

  const handleRetry = () => {
    // Stop polling if active
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    
    // Clear timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    // Stop timer
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    
    // Reset polling attempts
    pollAttemptsRef.current = 0;
    setIsCheckingStatus(false);
    
    // Clear stored payment data
    sessionStorage.removeItem('pendingOrderId');
    sessionStorage.removeItem('phonePeMerchantTransactionId');
    sessionStorage.removeItem('paymentInitiated');
    sessionStorage.removeItem('paymentTimestamp');
    sessionStorage.removeItem('pendingPaymentAmount');
   
    sessionStorage.removeItem('mandateUri');
    sessionStorage.removeItem('mandateUriOpened');
    sessionStorage.removeItem('shouldNavigateToProcessing');
    sessionStorage.removeItem('isUpiCollectFlow');
    
    // Navigate back to payment page
    navigate('/trial/subscription-payment');
  };

  // Format time remaining as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className={styles.container}>
      {/* Background Gradient SVG */}
      <div className={styles.backgroundGradient}>
        <svg xmlns="http://www.w3.org/2000/svg" width="360" height="684" viewBox="0 0 360 684" fill="none" className={styles.backgroundSvg}>
          <g filter="url(#filter0_fn_558_5971)">
            <circle cx="180" cy="92" r="158" fill="#4F5568"/>
          </g>
          <defs>
            <filter id="filter0_fn_558_5971" x="-412" y="-500" width="1184" height="1184" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
              <feFlood floodOpacity="0" result="BackgroundImageFix"/>
              <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
              <feGaussianBlur stdDeviation="217" result="effect1_foregroundBlur_558_5971"/>
              <feTurbulence type="fractalNoise" baseFrequency="0.5 0.5" stitchTiles="stitch" numOctaves="3" result="noise" seed="2135"/>
              <feComponentTransfer in="noise" result="coloredNoise1">
                <feFuncR type="linear" slope="2" intercept="-0.5"/>
                <feFuncG type="linear" slope="2" intercept="-0.5"/>
                <feFuncB type="linear" slope="2" intercept="-0.5"/>
                <feFuncA type="discrete" tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "/>
              </feComponentTransfer>
              <feComposite operator="in" in2="effect1_foregroundBlur_558_5971" in="coloredNoise1" result="noise1Clipped"/>
              <feComponentTransfer in="noise1Clipped" result="color1">
                <feFuncA type="table" tableValues="0 0.4"/>
              </feComponentTransfer>
              <feMerge result="effect2_noise_558_5971">
                <feMergeNode in="effect1_foregroundBlur_558_5971"/>
                <feMergeNode in="color1"/>
              </feMerge>
            </filter>
          </defs>
        </svg>
      </div>

      {/* Main Content */}
      <div className={styles.content}>
        {/* PhonePe Icon */}
        <div className={styles.iconContainer}>
          <img src={upiGroupImages} alt="UPI apps" className={styles.icon} />
        </div>

        {/* Title */}
        <h1 className={styles.title}>
          Open UPI app to <br /> complete payment of ₹{isTrialFlow ? 2 : amount}
        </h1>

        {/* Timer Box */}
        
          <div className={styles.timerBox}>
            <span className={styles.timerText}>Complete payment with in </span>
            <span className={styles.timerValue}> {formatTime(timeRemaining)}</span>
            <span className={styles.timerText}>  mins</span>
          </div>
        
        
        {/* Status checking indicator */}
        
      </div>

      {/* Warning Text */}
      <p className={styles.warningText}>
        Please do not close this until the <br /> transaction is complete
      </p>

      
    </div>
  );
};

export default PaymentWaiting;

